import Grid from "@mui/material/Grid2";
import {
  Autocomplete,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DuplicateMapDetails from "./DuplicateMapDetails";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { MapsEndpointLogic } from "../../../../../logic/DataCollection/Unifier/mapsEndpointLogic";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import {
  setOriginalParams,
  setSelectedDestinationTableIDMaps,
  setSelectedInterfaceIDMaps,
} from "../../../../../features/mapsSlice";

const tableHeadings = {
  destination_table_column: "Destination Table Column",
  source_field_expression: "Source Field Expression",
  calculation_type: "Calculation Type ",
  unique_definition: "Unique Definition",
};
const rows = [
  {
    destination_table_column: "activity_date",
    source_field_expression: "Please enter source expression",
    calculation_type: "Choose from list",
    unique_definition: "Choose from list",
  },
  {
    destination_table_column: "user_unique_id",
    source_field_expression: "Please enter source expression",
    calculation_type: "Choose from list",
    unique_definition: "Choose from list",
  },
  {
    destination_table_column: "registrations",
    source_field_expression: "Please enter source expression",
    calculation_type: "Choose from list",
    unique_definition: "Choose from list",
  },
];

const DuplicateMap = () => {
  const [searchParams] = useSearchParams();
  const oauthTokenHeader: any = useSelector(selectUserToken);
  const {
    mapDetails,
    interfaceData,
    destinationTablesData,
    mapDetailsDataPayload,
    isValidPayloadDetails,
  } = useSelector((state: any) => state.maps);
  const mapId: any = searchParams.get("mapId");
  const [navigatingBack] = useState(false);
  const mapName = searchParams.get("mapName");
  const [loading, setLoading] = useState(true);
  const [selectedInterface, setSelectedInterface] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [selectedDestinationTable, setSelectedDestinationTable] =
    useState(null);
  const [mapNameInput, setMapNameInput] = useState("");
  const dispatch = useDispatch();
  const originalParams = useSelector((state: any) => state.maps.originalParams);
  const navigate = useNavigate();
  const fetchMapsData = async () => {
    setLoading(true);
    try {
      await MapsEndpointLogic.getMapDetails(oauthTokenHeader, mapId);
      await Promise.all([
        MapsEndpointLogic.getInterface(oauthTokenHeader),
        MapsEndpointLogic.getDestinationTables(oauthTokenHeader),
        MapsEndpointLogic.getAllCalculationTypesMaps(oauthTokenHeader),
        MapsEndpointLogic.getUnifierUniqueDefinitionMaps(oauthTokenHeader),
      ]);
    } catch (error: any) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            error?.parsedText ||
            "Error while fetching data. Please try again later.",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchMapsData();

    // eslint-disable-next-line
  }, []);

  const handleDuplicateMap = async () => {
    setIsDuplicating(true);
    try {
      const payload = {
        map_name: mapNameInput.trim(),
        backend_interface_id: selectedInterfaceValue?.id,
        destination_table_id: selectedDestinationTableValue?.id,
        field_mapping: mapDetailsDataPayload,
      };
      const response = await MapsEndpointLogic.createMap(
        oauthTokenHeader,
        payload
      );
      if (response.status === 201) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Map Duplicated successfully",
            type: "success",
          })
        );
        navigate(`/data_collection/unifier/maps?${originalParams}`, {
          replace: true,
        });
      }
    } catch (error: any) {
      console.error("Error duplicating map:", error);
      dispatch(
        setDisplayErrorSnackBar({
          message:
            error?.message ||
            "Failed to duplicate map. Please check the details and try again.",
          type: "error",
        })
      );
    } finally {
      setIsDuplicating(false);
    }
  };

  const selectedInterfaceValue =
    selectedInterface &&
    interfaceData?.find(
      (item: any) => item.affiliate_system_interface_name === selectedInterface
    )
      ? {
          label: selectedInterface,
          id: interfaceData.find(
            (item: any) =>
              item.affiliate_system_interface_name === selectedInterface
          ).id,
        }
      : null;

  const selectedDestinationTableValue =
    selectedDestinationTable &&
    destinationTablesData?.find(
      (item: any) => item.destination_table === selectedDestinationTable
    )
      ? {
          label: selectedDestinationTable,
          id: destinationTablesData.find(
            (item: any) => item.destination_table === selectedDestinationTable
          ).id,
        }
      : null;
  // Prefill interface & destination table from mapDetails on load
  useEffect(() => {
    if (mapDetails?.map_name) {
      setMapNameInput(mapDetails.map_name + "_copy"); // optional suffix for duplicate
    }
    if (mapDetails?.backend_interface_id && interfaceData?.length > 0) {
      const matchedInterface = interfaceData.find(
        (item: any) => item.id === mapDetails.backend_interface_id
      );
      if (matchedInterface) {
        setSelectedInterface(matchedInterface.affiliate_system_interface_name);
        dispatch(setSelectedInterfaceIDMaps(matchedInterface.id));
      }
    }

    if (mapDetails?.destination_table_id && destinationTablesData?.length > 0) {
      const matchedDestTable = destinationTablesData.find(
        (item: any) => item.id === mapDetails.destination_table_id
      );
      if (matchedDestTable) {
        setSelectedDestinationTable(matchedDestTable.destination_table);
        dispatch(setSelectedDestinationTableIDMaps(matchedDestTable.id));
      }
    }
  }, [mapDetails, interfaceData, destinationTablesData, dispatch]);

  useEffect(() => {
    return () => {
      if (!navigatingBack) return;
      if (originalParams) {
        navigate(`/data_collection/unifier/maps?${originalParams}`, {
          replace: true,
        });
      } else {
        navigate(`/data_collection/unifier/maps`, { replace: true });
      }
      dispatch(setOriginalParams(""));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <>
          <Paper elevation={0} sx={{ padding: "12px 16px", mb: "16px" }}>
            <Grid container alignItems="center" columnSpacing={3}>
              <Grid size={12} mb={2}>
                <Typography variant="h3" sx={{ fontSize: "24px" }}>
                  Duplicate Map
                  <span
                    style={{
                      marginLeft: "10px",
                      fontSize: "14px",
                      color: "#000000DE",
                    }}
                  >
                    <strong style={{ fontWeight: "500" }}>Source Map:</strong>{" "}
                    {mapName} (ID {mapId})
                  </span>
                </Typography>
              </Grid>
              <Grid size={9}>
                <Stack
                  direction="row"
                  alignItems="flex-end"
                  justifyContent={"flex-end"}
                  gap={2}
                >
                  <TextField
                    required
                    label="Map Name" // Set your label here
                    placeholder="Please enter map name" // Set your placeholder here
                    color="secondary"
                    size="small"
                    fullWidth
                    value={mapNameInput}
                    onBlur={() =>
                      setMapNameInput((prev) => (prev ? prev.trim() : prev))
                    }
                    onChange={(e) => setMapNameInput(e.target.value)}
                  />
                  <Autocomplete
                    sx={{ width: "100%" }}
                    value={selectedInterfaceValue}
                    options={
                      interfaceData?.map((item: any) => ({
                        label: item.affiliate_system_interface_name,
                        id: item.id,
                      })) || []
                    }
                    onChange={(_event, newValue: any) => {
                      setSelectedInterface(newValue?.label);
                      dispatch(setSelectedInterfaceIDMaps(newValue?.id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Choose from the list"
                        label="Interface"
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ marginLeft: "12px" }} />
                            </InputAdornment>
                          ),
                        }}
                        color="secondary"
                      />
                    )}
                    size="small"
                  />

                  <Autocomplete
                    sx={{ width: "100%" }}
                    value={selectedDestinationTableValue}
                    options={
                      destinationTablesData
                        ?.filter(
                          (item: any) =>
                            item.destination_table !==
                            "daily_brand_campaign_performance"
                        )
                        .map((item: any) => ({
                          label: item.destination_table,
                          id: item.id,
                        })) || []
                    }
                    onChange={(_event, newValue: any) => {
                      setSelectedDestinationTable(newValue?.label);
                      dispatch(setSelectedDestinationTableIDMaps(newValue?.id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Choose from the list"
                        label="Destination Table"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ marginLeft: "12px" }} />
                            </InputAdornment>
                          ),
                        }}
                        color="secondary"
                      />
                    )}
                    size="small"
                  />
                </Stack>
              </Grid>
              <Grid size={3} textAlign={"right"}>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={
                    !mapNameInput ||
                    !selectedInterfaceValue ||
                    !selectedDestinationTableValue ||
                    !isValidPayloadDetails
                  }
                  onClick={handleDuplicateMap}
                  sx={{
                    padding: "8px 20px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Duplicate Map
                  {isDuplicating && <SimpleBackdrop />}
                  <ContentCopyIcon sx={{ width: "20px", ml: "5px" }} />
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Duplicate Map Details Table*/}
          <DuplicateMapDetails
            tableHeadings={tableHeadings}
            rows={rows}
            selectedInterface={selectedInterface}
            selectedDestinationTable={selectedDestinationTable}
          />
        </>
      )}
    </>
  );
};

export default DuplicateMap;
